import { useState } from 'react'
import { ContactSuggestInput } from './ContactSuggestInput'
import { m } from '../paraglide/messages'
import type { GoogleContact } from '../lib/api'

export interface StudentFormValues {
  name:  string
  phone: string
  email: string
  rate:  string
  notes: string
}

interface Props {
  initial?:     Partial<StudentFormValues>
  submitLabel:  string
  pendingLabel: string
  pending:      boolean
  error?:       string
  onSubmit:     (values: StudentFormValues) => void
  footer?:      React.ReactNode   // extra controls (e.g. Cancel / Dismiss)
}

const fieldCls = 'border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'

export function StudentForm({ initial, submitLabel, pendingLabel, pending, error, onSubmit, footer }: Props) {
  const [name, setName]   = useState(initial?.name  ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [rate, setRate]   = useState(initial?.rate  ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const pickContact = (c: GoogleContact) => {
    if (c.name)  setName(c.name)
    if (c.phone) setPhone(c.phone)
    if (c.email) setEmail(c.email)
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ name, phone, email, rate, notes }) }}
      className="flex flex-col gap-4 max-w-md"
    >
      <div className="flex flex-col gap-1">
        <label className="text-sm text-zinc-700" htmlFor="name">
          {m.form_name()} <span className="text-zinc-400">{m.studentform_name_hint()}</span>
        </label>
        <ContactSuggestInput id="name" value={name} onChange={setName} onPick={pickContact} className={fieldCls} />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm text-zinc-700" htmlFor="phone">
          {m.studentform_phone_label()} <span className="text-zinc-400">{m.studentform_phone_hint()}</span>
        </label>
        <ContactSuggestInput
          id="phone"
          type="tel"
          value={phone}
          onChange={setPhone}
          onPick={pickContact}
          placeholder="972501234567"
          className={fieldCls}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm text-zinc-700" htmlFor="email">{m.form_email()}</label>
        <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm text-zinc-700" htmlFor="rate">{m.form_rate()}</label>
        <input
          id="rate"
          type="number"
          min="0"
          step="any"
          required
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className={fieldCls}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm text-zinc-700" htmlFor="notes">{m.form_notes()}</label>
        <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={fieldCls} />
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={pending}
          className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending ? pendingLabel : submitLabel}
        </button>
        {footer}
      </div>
    </form>
  )
}
